import React from 'react';
import { Link } from 'react-router-dom';
import {
  ArrowLeft, BookOpen, ShoppingCart, Store, Truck, Database, MessageSquare,
  Zap, Sparkles,
} from 'lucide-react';

const CATEGORIES = [
  {
    slug: 'marketplaces',
    name: 'Marketplaces',
    color: '#3EF1C7',
    icon: ShoppingCart,
    desc: 'Sell on every major US marketplace from one catalog.',
    items: [
      { name: 'Amazon Seller Central (SP-API)', slug: 'amazon-seller', notes: 'Orders + listings + advertising + FBA' },
      { name: 'Amazon Vendor Central (EDI)', slug: 'amazon-vendor', notes: 'EDI 850/855/856/810 over AS2' },
      { name: 'Walmart Marketplace', slug: 'walmart', notes: 'Orders, items, inventory, WFS' },
      { name: 'eBay', slug: 'ebay', notes: 'Fulfillment + Trading APIs' },
      { name: 'Etsy (Open API v3)', slug: 'etsy', notes: 'Listings, receipts, inventory' },
      { name: 'Google Shopping (Merchant Center)', slug: 'google-shopping', notes: 'Product feed, ads, conversion tracking' },
      { name: 'Target Plus', slug: 'target-plus', notes: 'Mandatory EDI · invite-only' },
    ],
  },
  {
    slug: 'platforms',
    name: 'Platforms',
    color: '#7A5CFF',
    icon: Store,
    desc: 'Connect your existing storefront platform.',
    items: [
      { name: 'Shopify / Shopify Plus', slug: 'shopify-setup', notes: 'OAuth + GraphQL Admin API', external: '/docs/shopify-setup' },
      { name: 'BigCommerce', slug: 'bigcommerce', notes: 'REST V3 + V2' },
      { name: 'WooCommerce', slug: 'woocommerce', notes: 'REST API + webhooks' },
      { name: 'Magento / Adobe Commerce', slug: 'magento', notes: 'REST + SOAP' },
      { name: 'Squarespace Commerce', slug: 'squarespace', notes: 'Commerce API v1.0' },
    ],
  },
  {
    slug: 'warehousing',
    name: 'Warehousing & 3PL',
    color: '#FFD166',
    icon: Truck,
    desc: 'Pick a 3PL via our RFQ marketplace, or bring your own.',
    items: [
      { name: '3PL Marketplace overview', slug: 'wms-overview', notes: 'Post an RFQ → get bids in hours, not weeks' },
      { name: 'ShipBob', slug: 'shipbob', notes: 'Modern REST API · 2026-01 endpoints' },
      { name: 'ShipMonk', slug: 'shipmonk', notes: 'AI-powered 3PL · 200+ integrations' },
      { name: 'ShipHero', slug: 'shiphero', notes: 'GraphQL public API · owned US warehouses' },
      { name: 'Amazon MCF', slug: 'amazon-mcf', notes: 'Use Amazon FCs for non-Amazon orders' },
      { name: 'Flexport', slug: 'flexport', notes: 'Freight + parcel · global trade docs' },
      { name: 'Onboarding your own 3PL', slug: '3pl-onboarding', notes: 'For warehouses signing up to our marketplace' },
    ],
  },
  {
    slug: 'oms',
    name: 'OMS / ERP',
    color: '#F55454',
    icon: Database,
    desc: 'Connect enterprise OMS + ERP systems at scale.',
    items: [
      { name: 'NetSuite (SuiteTalk REST)', slug: 'netsuite', notes: 'Sales orders, inventory adjustments, SuiteQL' },
      { name: 'Manhattan Active Omni', slug: 'manhattan', notes: 'DOM + ATP + reservations' },
      { name: 'SAP S/4HANA Cloud', slug: 'sap-s4hana', notes: 'OData V4 over OAuth 2.0 · planned' },
      { name: 'Oracle Fusion Cloud', slug: 'oracle-fusion', notes: 'REST + FBDI + Business Events · planned' },
    ],
  },
];

export default function DocsIntegrationMap() {
  return (
    <div className="min-h-screen bg-[#0A0F1C] text-white">
      <header className="border-b border-[#22304A] bg-[#0D1628]">
        <div className="max-w-7xl mx-auto px-6 py-4 flex items-center justify-between">
          <Link to="/docs" className="flex items-center gap-2 text-sm text-[#94A3B8] hover:text-white">
            <ArrowLeft className="w-4 h-4" /> All docs
          </Link>
          <div className="flex items-center gap-2">
            <BookOpen className="w-5 h-5 text-[#3EF1C7]" />
            <span className="font-semibold">Integrations</span>
          </div>
        </div>
      </header>

      <div className="max-w-7xl mx-auto px-6 py-12">
        <h1 className="text-4xl font-bold mb-3">Integration Map.</h1>
        <p className="text-lg text-[#94A3B8] mb-12 max-w-3xl">
          Where do you sell? Where does your inventory live? What's your OMS? Pick a path and we've
          documented the full connection flow — with a consistent 6-section template so every guide
          looks the same.
        </p>

        <div className="grid md:grid-cols-2 gap-8">
          {CATEGORIES.map(cat => {
            const Icon = cat.icon;
            return (
              <div key={cat.slug} className="bg-[#0D1628] border border-[#22304A] rounded-xl p-6">
                <div className="flex items-center gap-3 mb-3">
                  <div className="p-2 rounded-lg" style={{ background: `${cat.color}20` }}>
                    <Icon className="w-5 h-5" style={{ color: cat.color }} />
                  </div>
                  <h2 className="text-xl font-bold" style={{ color: cat.color }}>{cat.name}</h2>
                </div>
                <p className="text-sm text-[#94A3B8] mb-4">{cat.desc}</p>
                <ul className="space-y-2">
                  {cat.items.map(item => (
                    <li key={item.slug}>
                      <Link
                        to={item.external || `/docs/integrations/${cat.slug}/${item.slug}`}
                        className="block bg-[#0A0F1C] border border-[#22304A] rounded-lg p-3 hover:border-[#3EF1C7]/40 transition"
                      >
                        <div className="font-medium text-sm">{item.name}</div>
                        <div className="text-xs text-[#64748B] mt-1">{item.notes}</div>
                      </Link>
                    </li>
                  ))}
                </ul>
              </div>
            );
          })}
        </div>

        <div className="mt-12 pt-8 border-t border-[#22304A] text-center">
          <Sparkles className="w-6 h-6 text-[#3EF1C7] mx-auto mb-3" />
          <p className="text-sm text-[#94A3B8] mb-4">
            Not seeing your platform? We add new adapters via the existing CreatorPlatformAdapter + ERPAdapter + ThreePLProvider base classes.
          </p>
          <Link to="/docs/api-integration" className="px-4 py-2 border border-[#22304A] rounded-lg text-sm hover:bg-[#11192A]">
            Connect via our public API instead →
          </Link>
        </div>
      </div>
    </div>
  );
}
